/**
 * The drop zone needs surfaces the five styling tokens don't hold directly — a
 * faint fill, a dashed border, a stepped-back caption. Deriving them from
 * upperTextColor keeps the zone legible on a white store and a black one alike,
 * which is the whole reason there is no colour setting for it (DIV-012).
 *
 * Theme colours arrive as #rgb, #rrggbb or the 8-digit #rrggbbaa the Design
 * page saves. Anything else — a named colour, an rgb() string a merchant typed
 * — is returned untouched rather than guessed at: a wrong colour is worse than
 * an un-tinted one.
 */
/** The six hex digits of a theme colour, or null when it is not hex at all. */
function hexDigits(colour: string): string | null {
  if (typeof colour !== "string") return null;
  const hex = colour.trim().replace("#", "");
  const expanded =
    hex.length === 3
      ? hex[0]! + hex[0]! + hex[1]! + hex[1]! + hex[2]! + hex[2]!
      : hex.length === 8
        ? hex.slice(0, 6)
        : hex;
  return expanded.length === 6 && /^[0-9a-fA-F]{6}$/.test(expanded) ? expanded : null;
}

function relativeLuminance(hex: string): number {
  const channel = (pair: string): number => {
    const v = parseInt(pair, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return (
    0.2126 * channel(hex.slice(0, 2)) +
    0.7152 * channel(hex.slice(2, 4)) +
    0.0722 * channel(hex.slice(4, 6))
  );
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const ha = hexDigits(a);
  const hb = hexDigits(b);
  if (ha === null || hb === null) return 1;
  const la = relativeLuminance(ha);
  const lb = relativeLuminance(hb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * 151 of the 892 installed production stores have PriceBackground set to exactly
 * their UpperBackground — the accent and the surface are the same colour. Drawing
 * the drop zone's "or browse" link in that accent makes it invisible on one store
 * in six, which is why it is guarded here rather than used raw.
 *
 * The bar is deliberately LOW (2:1, not a WCAG 4.5:1 pass). The accent is the
 * merchant's own brand colour and a mildly low-contrast one is still legible and
 * still theirs — the most common real palette, #4a90e2 on #f1f1f1, sits at ~2.8
 * and must keep its blue. What this catches is the accent that has effectively
 * vanished. The fallback is upperTextColor, the one pairing with the background
 * that is guaranteed readable because every element label already relies on it.
 */
const VISIBLE_ENOUGH = 2;

export function readableAccent(
  accent: string,
  background: string,
  fallback: string,
): string {
  return contrastRatio(accent, background) >= VISIBLE_ENOUGH ? accent : fallback;
}

export function withAlpha(colour: string, alpha: number): string {
  if (typeof colour !== "string") return colour;
  const hex = colour.trim().replace("#", "");
  const expanded =
    hex.length === 3
      ? hex[0]! + hex[0]! + hex[1]! + hex[1]! + hex[2]! + hex[2]!
      : hex.length === 8
        ? hex.slice(0, 6)
        : hex;

  if (expanded.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(expanded)) return colour;

  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
